import React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users2, Layers, Newspaper, Inbox, LogOut, Home as HomeIcon, Menu, X, ExternalLink, BookOpen, UserSquare2, LayoutGrid, Ticket, Database, Megaphone, Image as ImageIcon, Calendar, Search, Link2, Target, Sun, Activity, Briefcase, Wallet, Clock, Percent, FileSpreadsheet, Coins, CheckCircle2, Star, UserPlus, Users, ScrollText, ClipboardCheck, Landmark, Tag, Globe, Building2, FolderOpen, Package, Handshake, Plane, GraduationCap, ClipboardList, LogOut as LogOutIcon, User, HeartHandshake, TrendingUp, ArrowRightLeft, ShieldAlert, AlertTriangle, Bell, KeyRound, FileDown, UploadCloud, ShieldCheck } from 'lucide-react';
import { useAuth, isSuperAdmin } from '../../context/AuthContext';
import NotificationBell from '../../components/NotificationBell';
import CommandPalette from '../../components/CommandPalette';
import ProfileMenu from '../../components/admin/ProfileMenu';
import TourController from '../../components/TourController';

// Roles allowed per nav item. Empty/undefined = everyone with admin access.
// Items may declare a `group` — consecutive items in the same group render under one heading.
const NAV = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true, roles: null },

  // ── CRM ──
  { to: '/admin/my-day', label: 'My Day', icon: Sun, group: 'CRM', roles: ['super_admin', 'admin', 'staff'] },
  { to: '/admin/leads', label: 'Leads', icon: Target, group: 'CRM', roles: ['super_admin', 'admin', 'staff'] },
  { to: '/admin/crm', label: 'Tickets', icon: Ticket, group: 'CRM', roles: ['super_admin', 'admin', 'staff'] },
  { to: '/admin/calendar', label: 'Calendar', icon: Calendar, group: 'CRM', roles: ['super_admin', 'admin', 'staff'] },
  { to: '/admin/workload', label: 'Workload', icon: Activity, group: 'CRM', roles: ['super_admin', 'admin'] },
  { to: '/admin/broadcasts', label: 'Broadcasts', icon: Megaphone, group: 'CRM', roles: ['super_admin', 'admin', 'staff'] },
  { to: '/admin/hr', label: 'HR', icon: Briefcase, group: 'HR & Payroll', roles: ['super_admin', 'admin', 'hr', 'staff'] },
  { to: '/admin/team', label: 'My team', icon: Users, group: 'HR & Payroll', roles: ['super_admin', 'admin', 'hr', 'staff'] },
  { to: '/admin/recruitment', label: 'Recruitment', icon: UserPlus, group: 'HR & Payroll', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/onboarding', label: 'Onboarding', icon: UserPlus, group: 'HR & Payroll', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/letter-templates', label: 'Letter templates', icon: ScrollText, group: 'HR & Payroll', roles: ['super_admin', 'admin'] },
  { to: '/admin/attendance', label: 'Attendance', icon: Clock, group: 'HR & Payroll', roles: ['super_admin', 'admin', 'hr', 'staff'] },
  { to: '/admin/attendance-approvals', label: 'Attendance approvals', icon: CheckCircle2, group: 'HR & Payroll', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/performance', label: 'Performance', icon: Star, group: 'HR & Payroll', roles: ['super_admin', 'admin', 'hr', 'staff'] },
  { to: '/admin/payroll', label: 'Payroll', icon: Wallet, group: 'HR & Payroll', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/payroll-status', label: 'Payroll status', icon: ClipboardCheck, group: 'HR & Payroll', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/loans', label: 'Loans', icon: Coins, group: 'HR & Payroll', roles: ['super_admin', 'admin', 'hr', 'staff'] },
  { to: '/admin/tax-slabs', label: 'Tax slabs', icon: Percent, group: 'HR & Payroll', roles: ['super_admin', 'admin'] },
  { to: '/admin/statutory-reports', label: 'Statutory reports', icon: FileSpreadsheet, group: 'HR & Payroll', roles: ['super_admin', 'admin'] },
  { to: '/admin/accounts-queue', label: 'Accounts inbox', icon: Landmark, group: 'HR & Payroll', roles: ['super_admin', 'admin', 'accounts'] },

  // ── Global HR ──
  { to: '/hr/me', label: 'My workspace', icon: User, group: 'Global HR', roles: ['super_admin', 'admin', 'hr', 'staff', 'accounts', 'content_publisher'] },
  { to: '/admin/approvals-inbox', label: 'Approvals inbox', icon: Inbox, group: 'Global HR', roles: ['super_admin', 'admin', 'hr', 'staff'] },
  { to: '/admin/countries', label: 'Countries', icon: Globe, group: 'Global HR', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/org-hierarchy', label: 'Org hierarchy', icon: Building2, group: 'Global HR', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/employee-docs', label: 'Employee files', icon: FolderOpen, group: 'Global HR', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/assets', label: 'Assets', icon: Package, group: 'Global HR', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/hr-vendors', label: 'HR vendors', icon: Handshake, group: 'Global HR', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/travel', label: 'Travel & expenses', icon: Plane, group: 'Global HR', roles: ['super_admin', 'admin', 'staff', 'hr'] },
  { to: '/admin/training', label: 'Training', icon: GraduationCap, group: 'Global HR', roles: ['super_admin', 'admin', 'hr', 'staff'] },
  { to: '/admin/policies', label: 'Policies', icon: ClipboardList, group: 'Global HR', roles: ['super_admin', 'admin', 'hr', 'staff'] },
  { to: '/admin/offboarding', label: 'Offboarding', icon: LogOutIcon, group: 'Global HR', roles: ['super_admin', 'admin', 'hr'] },

  // ── HR Ops (extended) ──
  { to: '/admin/mgmt-dashboard', label: 'Global dashboard', icon: LayoutDashboard, group: 'HR Ops', roles: ['super_admin', 'admin', 'hr', 'accounts'] },
  { to: '/admin/benefits', label: 'Benefits', icon: HeartHandshake, group: 'HR Ops', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/promotions', label: 'Promotions', icon: TrendingUp, group: 'HR Ops', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/transfers', label: 'Transfers', icon: ArrowRightLeft, group: 'HR Ops', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/overtime', label: 'Overtime', icon: Clock, group: 'HR Ops', roles: ['super_admin', 'admin', 'hr', 'staff'] },
  { to: '/admin/reminders', label: 'Reminders radar', icon: Bell, group: 'HR Ops', roles: ['super_admin', 'admin', 'hr'] },

  // ── Compliance & Admin ──
  { to: '/admin/discipline', label: 'Discipline / Grievance', icon: ShieldAlert, group: 'Compliance', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/safety', label: 'Health & safety', icon: AlertTriangle, group: 'Compliance', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/audit-log', label: 'Audit log', icon: ScrollText, group: 'Compliance', roles: ['super_admin', 'admin'] },
  { to: '/admin/access-matrix', label: 'Access matrix', icon: ShieldCheck, group: 'Compliance', roles: ['super_admin', 'admin'] },
  { to: '/admin/office-admin', label: 'Office resources', icon: KeyRound, group: 'Admin', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/hr-reports', label: 'Reports', icon: FileDown, group: 'Admin', roles: ['super_admin', 'admin', 'hr'] },
  { to: '/admin/migration', label: 'Data migration', icon: UploadCloud, group: 'Admin', roles: ['super_admin', 'admin'] },

  // ── Newsroom ──
  { to: '/admin/news', label: 'Company News', icon: Newspaper, group: 'Newsroom', roles: ['super_admin', 'admin', 'content_publisher'] },
  { to: '/admin/sayings', label: 'Daily Quotes', icon: Newspaper, group: 'Newsroom', roles: ['super_admin', 'admin', 'content_publisher'] },
  { to: '/admin/galleries', label: 'Galleries', icon: ImageIcon, group: 'Newsroom', roles: ['super_admin', 'admin', 'content_publisher'] },

  // ── Site ──
  { to: '/admin/home', label: 'Homepage', icon: LayoutGrid, group: 'Site', roles: ['super_admin', 'admin', 'content_publisher'] },
  { to: '/admin/about', label: 'About Us', icon: BookOpen, group: 'Site', roles: ['super_admin', 'admin', 'content_publisher'] },
  { to: '/admin/people', label: 'Leadership & People', icon: UserSquare2, group: 'Site', roles: ['super_admin', 'admin', 'content_publisher'] },
  { to: '/admin/verticals', label: 'Business Verticals', icon: Layers, group: 'Site', roles: ['super_admin', 'admin', 'content_publisher'] },

  // ── System ──
  { to: '/admin/inbox', label: 'Inbox & Subscribers', icon: Inbox, group: 'System', roles: ['super_admin', 'admin', 'content_publisher'] },
  { to: '/admin/masters', label: 'Masters', icon: Database, group: 'System', roles: ['super_admin', 'admin'] },
  { to: '/admin/users', label: 'Users & Roles', icon: Users2, group: 'System', roles: ['super_admin'] },
  { to: '/admin/nav-labels', label: 'Rename Tabs', icon: Tag, group: 'System', roles: ['super_admin'] },

  // ── Integrations ──
  { to: '/admin/books-link', label: 'Emerald Books', icon: Link2, group: 'Integrations', roles: ['super_admin', 'admin'] },
];

const renderNavItems = (items, overrides, badges, onClick) => {
  // Render with group headers where the group changes between adjacent items
  const out = [];
  let prevGroup = null;
  items.forEach((n) => {
    const groupLabel = n.group ? (overrides[`group:${n.group}`] || n.group) : null;
    if (n.group && n.group !== prevGroup) {
      out.push(
        <div key={`hdr-${n.group}`} className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">
          {groupLabel}
        </div>,
      );
    }
    prevGroup = n.group || null;
    const label = overrides[n.to] || n.label;
    const badge = badges[n.to];
    out.push(
      <NavLink
        key={n.to}
        to={n.to}
        end={n.end}
        onClick={onClick}
        data-testid={`admin-nav-${n.to.replace('/admin', '').replace('/', '') || 'overview'}`}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${
            isActive ? 'bg-[#0f5c3d] text-white' : 'text-white/75 hover:text-white hover:bg-white/5'
          }`
        }
      >
        <n.icon className="w-4 h-4" />
        <span className="flex-1">{label}</span>
        {badge > 0 && (
          <span data-testid={`nav-badge-${n.to.replace(/\//g, '-')}`} className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </NavLink>,
    );
  });
  return out;
};

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [paletteOpener, setPaletteOpener] = React.useState(0);
  const [labelOverrides, setLabelOverrides] = React.useState({});
  const [badges, setBadges] = React.useState({});

  const items = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));

  React.useEffect(() => {
    const fetchBadges = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/self/badges`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('emerald_token')}` },
      })
        .then((r) => (r.ok ? r.json() : {}))
        .then((d) => setBadges({
          '/admin/approvals-inbox': d.approvals_inbox || 0,
          '/admin/policies': d.pending_policies || 0,
        }))
        .catch(() => {});
    };
    fetchBadges();
    const t = setInterval(fetchBadges, 60000);
    return () => clearInterval(t);
  }, []);

  React.useEffect(() => {
    // Load per-tenant nav label overrides so admin's renames take effect.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nav-labels`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('emerald_token')}` },
    })
      .then((r) => r.ok ? r.json() : { data: {} })
      .then((d) => setLabelOverrides(d?.data || {}))
      .catch(() => setLabelOverrides({}));
    // Listen for edits from the NavLabelsPage
    const onUpdate = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nav-labels`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('emerald_token')}` },
      }).then((r) => r.json()).then((d) => setLabelOverrides(d?.data || {})).catch(() => {});
    };
    window.addEventListener('nav-labels-updated', onUpdate);
    return () => window.removeEventListener('nav-labels-updated', onUpdate);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Fire a keyboard event so the global CommandPalette (which listens for Cmd/Ctrl+K)
  // opens when the on-screen search pill is clicked.
  const openPalette = () => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
  };

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

  return (
    <div className="min-h-screen bg-[#f5f8f6] flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-[#0b1a2e] text-white">
        <Link to="/" className="px-6 py-6 border-b border-white/10 flex items-center gap-3">
          <span style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500, fontSize: 32, lineHeight: 1 }}>E</span>
          <div className="leading-tight">
            <div className="text-sm font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Emerald Admin</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Internal</div>
          </div>
        </Link>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {renderNavItems(items, labelOverrides, badges)}
        </nav>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-[#0b1a2e] text-white flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500, fontSize: 24 }}>E</span>
          <span className="text-sm font-medium">Emerald Admin</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={openPalette}
            data-testid="mobile-search-button"
            className="p-2 text-white/80 hover:text-white"
            aria-label="Search"
          >
            <Search className="w-5 h-5" />
          </button>
          <NotificationBell variant="mobile" />
          <ProfileMenu />
          <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2" aria-label="toggle menu">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-x-0 top-[52px] z-20 bg-[#0b1a2e] text-white p-3 space-y-0.5 shadow-xl max-h-[calc(100vh-52px)] overflow-y-auto">
          {renderNavItems(items, labelOverrides, badges, () => setMobileOpen(false))}
        </div>
      )}

      <main className="flex-1 min-w-0 lg:pl-0 pt-14 lg:pt-0">
        {/* Desktop top bar with global search pill + notification bell */}
        <div className="hidden lg:flex items-center gap-3 px-10 pt-6">
          <button
            onClick={openPalette}
            data-testid="global-search-button"
            className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-white/70 hover:bg-white border border-slate-200 text-sm text-slate-500 hover:text-slate-700 transition text-left max-w-md"
          >
            <Search className="w-4 h-4 opacity-70" />
            <span className="flex-1">Search tickets, tasks, people, docs…</span>
            <kbd className="hidden md:inline-flex items-center px-1.5 py-0.5 rounded border border-slate-300 bg-slate-50 text-[10px] font-mono text-slate-500">
              {isMac ? '⌘K' : 'Ctrl K'}
            </kbd>
          </button>
          <div className="ml-auto flex items-center gap-2">
            <div className="rounded-full bg-[#0b1a2e] px-1">
              <NotificationBell variant="desktop" />
            </div>
            <div className="rounded-full bg-[#0b1a2e] px-1 py-1">
              <ProfileMenu />
            </div>
          </div>
        </div>
        <div className="p-6 lg:p-10 max-w-6xl mx-auto">
          <Outlet />
        </div>
      </main>
      <CommandPalette />
      <TourController variant="admin" />
    </div>
  );
};

export default AdminLayout;
